import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, MessageCircle, Star, Heart, Zap, Award, Sparkles, FileText, Wallet } from 'lucide-react';
import { rentalPlans, whatsappMessage, companyInfo, beneficiosComuns, requirements } from '../data/mock';

export const Plans = () => {
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [currentMotoIndex, setCurrentMotoIndex] = useState(0);

  // Imagens fixas das motos com suas cores
  const motosImages = [
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/rla84wx1_IMG_2395.png",
      cor: "Vermelha",
      modelo: "DK 160"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/vhx69jw8_IMG_2396.png",
      cor: "Azul",
      modelo: "DK 160"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/ch42em81_2025-05-19-13-36-09-Sem-Titulo-1.png",
      cor: "Preta",
      modelo: "DK 160"
    }
  ];

  // Carousel automático de motos (troca a cada 3 segundos)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMotoIndex((prev) => (prev + 1) % motosImages.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const getCurrentMoto = () => motosImages[currentMotoIndex];
  
  const openWhatsApp = (planName, planPrice) => {
    const message = `Quero mais informações sobre o ${planName} - ${planPrice}. ${whatsappMessage}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${encodedMessage}`, '_blank');
  };

  const planIcons = {
    0: Zap,      // Mensal
    1: Heart,    // Anual
    2: Award     // Fidelidade - MAIS POPULAR
  };

  // Marcar Fidelidade como destacado (o melhor plano)
  const plansWithHighlight = rentalPlans.map((plan) => ({
    ...plan,
    highlighted: plan.name === 'Plano Fidelidade'
  }));

  return (
    <>
      <style>{`
        @keyframes fadeInScale {
          0% { 
            opacity: 0; 
            transform: scale(0.9) translateY(10px); 
          }
          100% { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
      `}</style>
      <section id="planos" className="relative py-24 overflow-hidden">
      {/* Background with Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
          alt="Background"
          className="w-full h-full object-cover opacity-5"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-green-50/50" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-100 rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-emerald-100 rounded-full opacity-20 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 mb-6 shadow-sm">
            <Heart className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Preços que Cabem no Seu Bolso</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Escolha o Plano Ideal
            <br />
            <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Para Sua Necessidade
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Dois caminhos: <strong>aluguel comum</strong> (Mensal e Anual, com devolução ao final)
            ou <strong className="text-emerald-700">aluguel com aquisição</strong> (Fidelidade — a moto fica sua em 36 meses).
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-10 mb-16">
          {plansWithHighlight.map((plan, index) => {
            const PlanIcon = planIcons[index] || Star;
            const isHighlighted = plan.highlighted;
            const isHovered = hoveredPlan === plan.id;
            
            return (
              <Card 
                key={plan.id} 
                className={`
                  relative overflow-hidden transition-all duration-500 hover:shadow-2xl
                  ${isHighlighted ? 'scale-105 shadow-xl border-2 border-green-500' : 'hover:scale-105'}
                  ${isHovered ? 'shadow-2xl' : ''}
                `}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  isHighlighted 
                    ? 'from-green-50 via-emerald-50 to-teal-50' 
                    : 'from-gray-50 to-white'
                } opacity-50`} />
                
                {/* Popular Badge */}
                {isHighlighted && (
                  <div className="absolute -top-0 left-0 right-0">
                    <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-600 text-white py-2 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-4 h-4 fill-current" />
                        <span className="text-sm font-extrabold uppercase tracking-wide">A moto fica sua ao final!</span>
                        <Sparkles className="w-4 h-4 fill-current" />
                      </div>
                    </div>
                  </div>
                )}
                
                <CardHeader className={`relative text-center ${isHighlighted ? 'pt-16' : 'pt-8'}`}>
                  {/* Plan Icon */}
                  <div className={`mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-6 ${
                    isHighlighted
                      ? 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500'
                      : 'bg-gradient-to-br from-slate-700 to-slate-900'
                  }`}>
                    <PlanIcon className="w-8 h-8 text-white" />
                  </div>
                  
                  <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{plan.name}</CardTitle>

                  {/* Tipo de aluguel — diferenciação cristalina */}
                  <div className="flex justify-center mb-3">
                    {plan.purchaseAtEnd ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1">
                        <Sparkles className="w-3.5 h-3.5" />
                        Aluguel com aquisição
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                        Aluguel comum
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-5 px-2">{plan.description}</p>

                  {/* Preço Semanal — destaque principal */}
                  <div className={`mx-auto rounded-2xl p-5 mb-3 ${
                    isHighlighted
                      ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300'
                      : 'bg-slate-50 border border-slate-200'
                  }`}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Valor semanal</div>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-sm font-medium text-gray-600">R$</span>
                      <span className={`text-5xl font-extrabold leading-none ${
                        isHighlighted
                          ? 'bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent'
                          : 'text-gray-900'
                      }`}>
                        {plan.weeklyPrice.toFixed(0)}
                      </span>
                      <span className="text-sm font-medium text-gray-600">/semana</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      ≈ R$ {plan.dailyPrice.toFixed(2).replace('.', ',')}/dia
                    </div>
                  </div>

                  {/* Período mínimo */}
                  <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <span className="text-gray-500">Permanência mínima:</span>
                    <span className={isHighlighted ? 'text-emerald-700' : 'text-gray-900'}>{plan.period}</span>
                  </div>

                  {/* Como funciona */}
                  <p className="text-xs text-gray-600 italic px-2">{plan.summary}</p>
                </CardHeader>
                
                <CardContent className="relative space-y-5 px-6 pb-8 pt-2">
                  {/* Selo destaque Fidelidade */}
                  {plan.purchaseAtEnd && (
                    <div className="rounded-xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-500 to-green-600 text-white px-4 py-3 text-center shadow-md">
                      <div className="flex items-center justify-center gap-2">
                        <Award className="w-5 h-5" />
                        <span className="text-sm font-extrabold uppercase tracking-wide">Ao final, a moto é sua!</span>
                      </div>
                    </div>
                  )}

                  {/* Features List */}
                  <div className="space-y-2.5">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">O que está incluso</div>
                    {plan.included.map((feature, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/80 transition-colors duration-200 group"
                      >
                        <CheckCircle className={`mt-0.5 w-5 h-5 flex-shrink-0 ${
                          isHighlighted ? 'text-emerald-500' : 'text-green-500'
                        }`} />
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* CTA Button */}
                  <Button 
                    onClick={() => openWhatsApp(plan.name, `R$ ${plan.weeklyPrice}/semana`)}
                    data-testid={`plan-cta-${plan.id}`}
                    className={`
                      w-full py-6 rounded-xl font-semibold text-base shadow-lg transition-all duration-300
                      ${isHighlighted 
                        ? 'bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-600 bg-[length:200%_auto] hover:bg-right text-white hover:shadow-xl' 
                        : 'bg-gray-900 hover:bg-gray-800 text-white hover:shadow-xl'
                      }
                      hover:scale-105 group
                    `}
                  >
                    <MessageCircle className="w-5 h-5 mr-2 inline" />
                    <span className="group-hover:underline decoration-2 underline-offset-4">
                      {isHighlighted ? 'Quero este plano!' : 'Contratar agora'}
                    </span>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Benefícios Comuns — válidos para todos os planos */}
        <div className="bg-white rounded-3xl border border-emerald-100 shadow-xl p-8 md:p-10 mb-10" data-testid="beneficios-comuns">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 mb-3">
              <Heart className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Vale para todos os planos</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
              Benefícios inclusos
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {beneficiosComuns.map((b, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 hover:shadow-md transition-all"
              >
                <div className="text-4xl mb-3">{b.icon}</div>
                <h4 className="font-bold text-gray-900 text-lg mb-1">{b.title}</h4>
                <p className="text-sm text-gray-600">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Requisitos para contratação */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 md:p-10 mb-12" data-testid="requisitos-contratacao">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Requisitos para contratação
            </h3>
            <p className="text-sm text-gray-600">Tenha estes documentos em mãos para uma retirada rápida</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Documentação */}
            <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-gray-900 text-lg">Documentação</h4>
              </div>
              <ul className="space-y-2.5">
                {requirements.documents.map((doc, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pagamento inicial */}
            <div className="rounded-2xl border-2 border-emerald-200 p-6 bg-gradient-to-br from-emerald-50 to-green-50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center">
                  <Wallet className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-gray-900 text-lg">Pagamento inicial</h4>
              </div>
              <ul className="space-y-2.5">
                {requirements.financial.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-emerald-200">
                <p className="text-xs text-gray-600 italic">
                  💡 O caução de R$ 700,00 é devolvido ao final do contrato (descontado eventuais débitos).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl mb-12">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            🎯 Por Que Somos a Melhor Escolha?
          </h3>
          <p className="text-lg md:text-xl opacity-95 max-w-3xl mx-auto mb-8">
            Após pesquisa de mercado, confirmamos: <strong>nossos preços são os mais competitivos da Baixada Santista!</strong> 
            Além de economia, você tem suporte humanizado e cuidado em cada etapa.
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: '💚', title: 'Preços Justos', desc: 'Mais baixos da região' },
              { icon: '🤝', title: 'Suporte Humano', desc: 'Sempre prontos para ajudar' },
              { icon: '🛡️', title: 'Segurança Total', desc: 'Seguro e IPVA incluídos' },
              { icon: '⭐', title: 'Satisfação Garantida', desc: 'Clientes felizes sempre' }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105 border border-white/20"
              >
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h4 className="font-bold text-lg mb-1">{feature.title}</h4>
                <p className="text-sm opacity-90">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};